import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Route, Routes } from 'react-router-dom';

// Third-party packages.
import Helmet from 'react-helmet';

// Api.
import AgentApi from '../../../api/instance/AgentApi';

// Types.
import Agent from '../../../types/instance/Agent';

// Context.
import { useAgents } from '../../../context/AgentContext';

// Constants.
import { DATA_INITIALISING, DATA_LOADING, DATA_LOADED } from '../../../component/Constants';

// Views.
import List from './List';
import Detail from './Detail';
import Breadcrumb from "../../../container/breadcrumb";
import AdminTrail from "../../../container/breadcrumb/AdminTrail";
import HeaderActions from "../../../container/header_actions";
import ConditionalRender from "../../../helpers/ConditionalRender";

interface IAgentsProps {
    agents: Agent[],
    toggleMenu: Function,
}

function Agents(props: IAgentsProps) {
    const { agents, setAgents } = useAgents();
    const [loading, setLoading] = useState<number>(DATA_INITIALISING);

    const getData = async () => {
        setLoading(DATA_LOADING);

        const agentsData = await AgentApi.getAgents();

        // Fetch counts for each agent
        const agentsWithCounts = await Promise.all(
            agentsData.map(async (agent) => {
                const counts = await AgentApi.getAgentCounts(agent.id);
                return { ...agent, ...counts };
            })
        );

        setLoading(DATA_LOADED);
        setAgents(agentsWithCounts || []);
    };

    useEffect(() => {
        if (loading === DATA_INITIALISING) {
            getData();
        }
    }, [loading]);

    const refresh = () => {
        getData();
    };

    return (
        <div id="content">
            <Helmet>
                <title>Agents - Settings - DBmarlin</title>
                <meta name="description" content="Manage agents" />
            </Helmet>

            <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                <Breadcrumb heading="Agents">
                    {[<AdminTrail key="agents-trail" section="Agents" sectionPath="/admin/agents"
                                  resolveEntityLabel={(id) =>
                                      agents.find(agent => agent.id === Number(id))?.name}/>]}
                </Breadcrumb>
                <HeaderActions toggleMenu={props.toggleMenu} refresh={refresh}/>
            </div>

            <div className="loader">
                <ConditionalRender if={false}>
                    <div className="bar"></div>
                </ConditionalRender>
            </div>

            <div className="row row-cols-1">
                <Routes>
                    <Route path="/:actionId/:agentId" element={<Detail />} />
                    <Route path="/" element={<List loading={loading} agents={agents} refresh={refresh} />} />
                </Routes>
            </div>
        </div>
    );
}

Agents.propTypes = {
    toggleMenu: PropTypes.func.isRequired,
};

export default Agents;